import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useCompanies from "../../api/company";
import useFiles from "../../api/files";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Building2,
  Upload,
  Download,
  Trash2,
  File,
  FileText,
  Image,
  FileArchive,
  AlertCircle,
  Check,
  Loader2,
} from "lucide-react";

const AdminFilesManager = () => {
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getAllCompanies } = useCompanies();
  const {
    getFilesByCompanyId,
    uploadFileForCompany,
    deleteFile,
    downloadFile,
  } = useFiles();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchFiles();
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    try {
      const data = await getAllCompanies();
      setCompanies(data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setError("Kon bedrijven niet ophalen");
    }
  };

  const fetchFiles = async () => {
    if (!selectedCompanyId) return;

    setLoading(true);
    try {
      const data = await getFilesByCompanyId(selectedCompanyId);
      console.log("Fetched files data:", data);
      // Ensure data is always an array
      setFiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching files:", error);
      setError("Kon bestanden niet ophalen");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        setError("Bestand is te groot. Maximaal 50MB toegestaan.");
        return;
      }
      setSelectedFile(file);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCompanyId) {
      setError("Selecteer een bestand en bedrijf");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("description", fileDescription);

      await uploadFileForCompany(selectedCompanyId, formData);

      setSuccess("Bestand succesvol geüpload!");
      setSelectedFile(null);
      setFileDescription("");

      // Reset file input
      const fileInput = document.getElementById("file-upload");
      if (fileInput) fileInput.value = "";

      // Refresh files list
      fetchFiles();
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(error.response?.data?.message || "Kon bestand niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const ok = await downloadFile(fileId);
      if (ok) {
        setSuccess("Download gestart (S3 URL)...");
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error.message || "Kon bestand niet downloaden");
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    try {
      await deleteFile(fileToDelete.id);
      setSuccess("Bestand succesvol verwijderd!");
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      setError("Kon bestand niet verwijderen");
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
      return (
        <Image
          className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-500"}`}
        />
      );
    }
    if (["pdf"].includes(ext)) {
      return <FileText className="h-4 w-4 text-red-400" />;
    }
    if (["zip", "rar", "7z"].includes(ext)) {
      return <FileArchive className="h-4 w-4 text-yellow-400" />;
    }
    return (
      <File
        className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
      />
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (!isAdmin) {
    return (
      <div
        className={`p-4 text-[13px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
      >
        Toegang geweigerd. Alleen voor administrators.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
        >
          Admin Bestandsbeheer
        </h1>
        <p
          className={`text-[13px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Upload bestanden voor klanten zoals logo's, brochures en documenten.
        </p>
      </div>

      {/* Notifications */}
      {error && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <Check className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Company Selection */}
      <div
        className={`rounded-xl p-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <Building2
            className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          />
          <span
            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Selecteer Bedrijf
          </span>
        </div>
        <Select
          value={selectedCompanyId ? String(selectedCompanyId) : ""}
          onValueChange={(value) => setSelectedCompanyId(value)}
        >
          <SelectTrigger
            className={`w-full md:w-[300px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <SelectValue placeholder="Kies een bedrijf..." />
          </SelectTrigger>
          <SelectContent
            className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
          >
            {companies.map((company) => (
              <SelectItem key={company.id} value={String(company.id)}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCompanyId && (
        <>
          {/* Upload Section */}
          <div
            className={`rounded-xl p-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Upload
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              />
              <span
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Bestand Uploaden
              </span>
            </div>
            <p
              className={`text-[12px] mb-4 ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              Upload een bestand voor{" "}
              {
                companies.find(
                  (c) => String(c.id) === String(selectedCompanyId),
                )?.name
              }
            </p>

            <div className="space-y-3">
              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Bestand
                </label>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className={`w-full text-[13px] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[12px] file:font-medium cursor-pointer ${isDark ? "text-gray-400 file:bg-white/[0.06] file:text-gray-300" : "text-gray-600 file:bg-gray-100 file:text-gray-700"}`}
                />
                {selectedFile && (
                  <p
                    className={`text-[12px] mt-1 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                  >
                    Geselecteerd: {selectedFile.name} (
                    {formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              <div>
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Beschrijving (optioneel)
                </label>
                <input
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder="Bijvoorbeeld: Logo 2024, Brochure Q1..."
                  disabled={uploading}
                  className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                />
              </div>

              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Uploaden...
                  </>
                ) : (
                  <>
                    <Upload className="h-3.5 w-3.5" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Files List */}
          <div
            className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <File
                  className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                />
                <span
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Bestanden
                </span>
              </div>
              {files.length > 0 && (
                <span
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600"}`}
                >
                  {files.length} {files.length === 1 ? "bestand" : "bestanden"}
                </span>
              )}
            </div>

            <div>
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <div
                    className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                  />
                </div>
              ) : files.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div
                    className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                  >
                    <File
                      className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    />
                  </div>
                  <p
                    className={`text-[13px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                  >
                    Nog geen bestanden geüpload voor dit bedrijf
                  </p>
                </div>
              ) : (
                <div className="space-y-2 p-4">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleDownload(file.id)}
                      className={`rounded-xl overflow-hidden cursor-pointer transition-colors ${isDark ? "bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12]" : "bg-white border border-gray-200/80 hover:border-gray-300"}`}
                    >
                      <div className="p-3">
                        <div className="flex items-center gap-3">
                          {/* File Icon */}
                          <div
                            className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                          >
                            {getFileIcon(file.filename)}
                          </div>

                          {/* File Info */}
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {file.description || file.filename}
                            </p>
                            <div
                              className={`flex items-center gap-2 mt-0.5 text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                            >
                              <span>{formatFileSize(file.filesize)}</span>
                              <span>·</span>
                              <span>
                                {formatDate(file.uploadedAt || file.created_at)}
                              </span>
                            </div>
                            {file.description && (
                              <p
                                className={`text-[11px] truncate mt-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              >
                                {file.filename}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file.id);
                              }}
                              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(file);
                              }}
                              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteDialogOpen(false)}
          />
          <div
            className={`relative rounded-xl p-6 w-full max-w-sm mx-4 ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
          >
            <h3
              className={`text-[14px] font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Bestand verwijderen
            </h3>
            <p
              className={`text-[13px] mb-5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Weet u zeker dat u "{fileToDelete?.filename}" wilt verwijderen?
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="h-9 px-4 rounded-lg text-[13px] font-medium bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFilesManager;
